import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, RefreshCw, DollarSign, CheckCircle, XCircle, Edit2, Save, X, ChevronDown, ChevronUp, User, EyeOff, Eye } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCosts = () => {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingCommitment, setEditingCommitment] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editCount, setEditCount] = useState("");
  const [editingCost, setEditingCost] = useState(null);
  const [editCostData, setEditCostData] = useState({
    title: "",
    description: "",
    amount: "",
    due_date: ""
  });
  const [expandedCost, setExpandedCost] = useState(null);
  const [commitments, setCommitments] = useState({});
  const [loadingCommitments, setLoadingCommitments] = useState({});
  const [editingIndividualCommitment, setEditingIndividualCommitment] = useState(null);
  const [editIndividualData, setEditIndividualData] = useState({ name: "", amount: "" });
  const [showCostsPublic, setShowCostsPublic] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [newCost, setNewCost] = useState({
    title: "",
    description: "",
    amount: "",
    due_date: ""
  });

  const fetchCosts = async () => {
    try {
      setLoading(true);
      // Admin view includes hidden costs
      const response = await axios.get(`${BACKEND_URL}/api/upcoming-costs?include_hidden=true`);
      setCosts(response.data);
    } catch (err) {
      toast.error("Error fetching costs");
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setShowCostsPublic(response.data.show_upcoming_costs);
    } catch (err) {
      console.error("Error fetching settings");
    }
  };

  useEffect(() => {
    fetchCosts();
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newCost.title || !newCost.amount) {
      toast.error("Title and amount are required");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${BACKEND_URL}/api/upcoming-costs`, {
        title: newCost.title,
        description: newCost.description || null,
        amount: parseFloat(newCost.amount),
        due_date: newCost.due_date || null
      });
      toast.success("Cost added successfully");
      setNewCost({ title: "", description: "", amount: "", due_date: "" });
      fetchCosts();
    } catch (err) {
      toast.error("Error adding cost");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (costId) => {
    if (!window.confirm("Are you sure you want to delete this cost?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/upcoming-costs/${costId}`);
      toast.success("Cost deleted");
      fetchCosts();
    } catch (err) {
      toast.error("Error deleting cost");
    }
  };

  const handleToggleCover = async (costId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/upcoming-costs/${costId}/cover`);
      fetchCosts();
    } catch (err) {
      toast.error("Error updating cost");
    }
  };

  const handleToggleHideCost = async (costId, currentHidden) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/upcoming-costs/${costId}/hide`);
      toast.success(currentHidden ? "Cost unhidden" : "Cost hidden from public");
      fetchCosts();
    } catch (err) {
      toast.error("Error updating cost");
    }
  };

  const toggleCostsVisibility = async () => {
    try {
      setSettingsLoading(true);
      const newValue = !showCostsPublic;
      await axios.patch(`${BACKEND_URL}/api/settings`, {
        show_bank_balance: true, // Keep other setting unchanged - we'll fetch it properly
        show_upcoming_costs: newValue
      });
      // Fetch full settings to ensure we have correct values
      const response = await axios.get(`${BACKEND_URL}/api/settings`);
      setShowCostsPublic(response.data.show_upcoming_costs);
      toast.success(newValue ? "Upcoming costs section is now visible" : "Upcoming costs section is now hidden");
    } catch (err) {
      toast.error("Error updating visibility");
    } finally {
      setSettingsLoading(false);
    }
  };

  const startEditingCommitment = (cost) => {
    setEditingCommitment(cost.id);
    setEditAmount(cost.committed_amount?.toString() || "0");
    setEditCount(cost.commitment_count?.toString() || "0");
  };

  const cancelEditingCommitment = () => {
    setEditingCommitment(null);
    setEditAmount("");
    setEditCount("");
  };

  const saveCommitment = async (costId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/upcoming-costs/${costId}/commitment`, {
        committed_amount: parseFloat(editAmount) || 0,
        commitment_count: parseInt(editCount) || 0
      });
      toast.success("Commitment updated");
      setEditingCommitment(null);
      fetchCosts();
    } catch (err) {
      toast.error("Error updating commitment");
    }
  };

  const startEditingCost = (cost) => {
    setEditingCost(cost.id);
    setEditCostData({
      title: cost.title || "",
      description: cost.description || "",
      amount: cost.amount?.toString() || "",
      due_date: cost.due_date || ""
    });
  };

  const cancelEditingCost = () => {
    setEditingCost(null);
    setEditCostData({ title: "", description: "", amount: "", due_date: "" });
  };

  const saveCostEdit = async (costId) => {
    if (!editCostData.title || !editCostData.amount) {
      toast.error("Title and amount are required");
      return;
    }
    
    try {
      await axios.put(`${BACKEND_URL}/api/upcoming-costs/${costId}`, {
        title: editCostData.title,
        description: editCostData.description || null,
        amount: parseFloat(editCostData.amount),
        due_date: editCostData.due_date || null
      });
      toast.success("Cost updated successfully");
      setEditingCost(null);
      fetchCosts();
    } catch (err) {
      toast.error("Error updating cost");
    }
  };

  const fetchCommitments = async (costId) => {
    try {
      setLoadingCommitments(prev => ({ ...prev, [costId]: true }));
      const response = await axios.get(`${BACKEND_URL}/api/upcoming-costs/${costId}/commitments`);
      setCommitments(prev => ({ ...prev, [costId]: response.data }));
    } catch (err) {
      toast.error("Error fetching commitments");
    } finally {
      setLoadingCommitments(prev => ({ ...prev, [costId]: false }));
    }
  };

  const toggleExpandCost = (costId) => {
    if (expandedCost === costId) {
      setExpandedCost(null);
    } else {
      setExpandedCost(costId);
      if (!commitments[costId]) {
        fetchCommitments(costId);
      }
    }
  };

  const startEditingIndividualCommitment = (commitment) => {
    setEditingIndividualCommitment(commitment.id);
    setEditIndividualData({
      name: commitment.name || "",
      amount: commitment.amount?.toString() || ""
    });
  };

  const cancelEditingIndividualCommitment = () => {
    setEditingIndividualCommitment(null);
    setEditIndividualData({ name: "", amount: "" });
  };

  const saveIndividualCommitment = async (commitmentId, costId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/commitments/${commitmentId}`, {
        name: editIndividualData.name,
        amount: parseFloat(editIndividualData.amount)
      });
      toast.success("Commitment updated");
      setEditingIndividualCommitment(null);
      fetchCommitments(costId);
      fetchCosts(); // Refresh totals
    } catch (err) {
      toast.error("Error updating commitment");
    }
  };

  const deleteIndividualCommitment = async (commitmentId, costId) => {
    if (!window.confirm("Delete this commitment?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/commitments/${commitmentId}`);
      toast.success("Commitment deleted");
      fetchCommitments(costId);
      fetchCosts(); // Refresh totals
    } catch (err) {
      toast.error("Error deleting commitment");
    }
  };

  const toggleHideCommitment = async (commitmentId, costId, currentHidden) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/commitments/${commitmentId}/hide`);
      toast.success(currentHidden ? "Commitment unhidden" : "Commitment hidden");
      fetchCommitments(costId);
      fetchCosts(); // Refresh totals
    } catch (err) {
      toast.error("Error updating commitment");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12" data-testid="admin-costs-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
            Admin Panel
          </span>
          <h1 className="font-['Bebas_Neue'] text-5xl text-white mb-4">
            Manage Upcoming Costs
          </h1>
          <p className="text-[#A3A3A3]">
            Add, remove, and manage upcoming costs displayed on the Donate page.
          </p>
        </motion.div>

        {/* Visibility Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="card-tactical p-6 mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold mb-1">Show on Public Website</h3>
              <p className="text-[#666] text-xs">
                {showCostsPublic 
                  ? "Upcoming costs section is visible on the Donate page" 
                  : "Upcoming costs section is hidden from the Donate page"}
              </p>
            </div>
            <button
              onClick={toggleCostsVisibility}
              disabled={settingsLoading}
              className={`relative w-14 h-8 rounded-full transition-colors ${
                showCostsPublic ? 'bg-green-600' : 'bg-[#333]'
              }`}
            >
              <span
                className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform flex items-center justify-center ${
                  showCostsPublic ? 'left-7' : 'left-1'
                }`}
              >
                {showCostsPublic ? (
                  <Eye className="w-3 h-3 text-green-600" />
                ) : (
                  <EyeOff className="w-3 h-3 text-[#666]" />
                )}
              </span>
            </button>
          </div>
        </motion.div>

        {/* Add New Cost Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-tactical p-6 mb-8"
        >
          <h2 className="font-['Bebas_Neue'] text-2xl text-white mb-4 flex items-center gap-2">
            <Plus className="w-6 h-6 text-[#FFD700]" />
            Add New Cost
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[#A3A3A3] text-sm font-mono uppercase tracking-widest mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newCost.title}
                  onChange={(e) => setNewCost({ ...newCost, title: e.target.value })}
                  placeholder="e.g., Vet Bill - Skipper"
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#4A5320] text-white focus:outline-none focus:border-[#FFD700]"
                />
              </div>
              <div>
                <label className="block text-[#A3A3A3] text-sm font-mono uppercase tracking-widest mb-2">
                  Amount *
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#666]" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newCost.amount}
                    onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3 bg-[#0A0A0A] border border-[#4A5320] text-white focus:outline-none focus:border-[#FFD700]"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-[#A3A3A3] text-sm font-mono uppercase tracking-widest mb-2">
                Description (optional)
              </label>
              <input
                type="text"
                value={newCost.description}
                onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
                placeholder="e.g., Emergency surgery needed"
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#4A5320] text-white focus:outline-none focus:border-[#FFD700]"
              />
            </div>
            
            <div>
              <label className="block text-[#A3A3A3] text-sm font-mono uppercase tracking-widest mb-2">
                Due Date (optional)
              </label>
              <input
                type="text"
                value={newCost.due_date}
                onChange={(e) => setNewCost({ ...newCost, due_date: e.target.value })}
                placeholder="e.g., May 1, 2026"
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#4A5320] text-white focus:outline-none focus:border-[#FFD700]"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Add Cost
                </>
              )}
            </button>
          </form>
        </motion.div>

        {/* Existing Costs List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactical p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Bebas_Neue'] text-2xl text-white">
              Current Costs ({costs.length})
            </h2>
            <button
              onClick={fetchCosts}
              className="text-[#FFD700] hover:text-white transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 text-[#FFD700] animate-spin" />
            </div>
          ) : costs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#A3A3A3]">No upcoming costs added yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {costs.map((cost) => (
                <div
                  key={cost.id}
                  className={`p-4 border transition-all ${
                    cost.hidden
                      ? 'border-[#333] bg-[#0A0A0A]/50 opacity-70'
                      : cost.is_covered 
                        ? 'border-green-700 bg-green-900/20' 
                        : 'border-[#4A5320] bg-[#0A0A0A]'
                  }`}
                >
                  {editingCost === cost.id ? (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[#A3A3A3] text-xs font-mono uppercase tracking-widest mb-1">
                            Title *
                          </label>
                          <input
                            type="text"
                            value={editCostData.title}
                            onChange={(e) => setEditCostData({ ...editCostData, title: e.target.value })}
                            className="w-full px-3 py-2 bg-[#1A1C18] border border-[#4A5320] text-white text-sm focus:outline-none focus:border-[#FFD700]"
                          />
                        </div>
                        <div>
                          <label className="block text-[#A3A3A3] text-xs font-mono uppercase tracking-widest mb-1">
                            Amount *
                          </label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#666]" />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editCostData.amount}
                              onChange={(e) => setEditCostData({ ...editCostData, amount: e.target.value })}
                              className="w-full pl-9 pr-3 py-2 bg-[#1A1C18] border border-[#4A5320] text-white text-sm focus:outline-none focus:border-[#FFD700]"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-[#A3A3A3] text-xs font-mono uppercase tracking-widest mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={editCostData.description}
                          onChange={(e) => setEditCostData({ ...editCostData, description: e.target.value })}
                          className="w-full px-3 py-2 bg-[#1A1C18] border border-[#4A5320] text-white text-sm focus:outline-none focus:border-[#FFD700]"
                        />
                      </div>
                      <div>
                        <label className="block text-[#A3A3A3] text-xs font-mono uppercase tracking-widest mb-1">
                          Due Date
                        </label>
                        <input
                          type="text"
                          value={editCostData.due_date}
                          onChange={(e) => setEditCostData({ ...editCostData, due_date: e.target.value })}
                          placeholder="e.g., May 1, 2026"
                          className="w-full px-3 py-2 bg-[#1A1C18] border border-[#4A5320] text-white text-sm focus:outline-none focus:border-[#FFD700]"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveCostEdit(cost.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-black font-mono text-xs uppercase tracking-widest hover:bg-[#e5c200] transition-colors"
                        >
                          <Save className="w-4 h-4" /> Save Changes
                        </button>
                        <button
                          onClick={cancelEditingCost}
                          className="flex items-center gap-2 px-4 py-2 border border-[#4A5320] text-[#A3A3A3] font-mono text-xs uppercase tracking-widest hover:text-white transition-colors"
                        >
                          <X className="w-4 h-4" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Display Mode */
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`font-semibold ${cost.hidden ? 'text-[#666]' : cost.is_covered ? 'text-green-400' : 'text-white'}`}>
                            {cost.title}
                          </h3>
                          {cost.hidden && (
                            <span className="text-xs bg-[#333] text-[#888] px-2 py-0.5 rounded">
                              HIDDEN
                            </span>
                          )}
                          {cost.is_covered && (
                            <span className="text-xs bg-green-700 text-green-100 px-2 py-0.5 rounded">
                              COVERED
                            </span>
                          )}
                        </div>
                        {cost.description && (
                          <p className="text-[#A3A3A3] text-sm mt-1">{cost.description}</p>
                        )}
                        {cost.due_date && (
                          <p className="text-[#666] text-xs mt-1">Due: {cost.due_date}</p>
                        )}
                        
                        {/* Commitment Section */}
                        <div className="mt-2 pt-2 border-t border-[#333]">
                          <div className="flex items-center justify-between">
                            <div>
                              {cost.committed_amount > 0 ? (
                                <p className="text-green-400 text-xs">
                                  ${cost.committed_amount.toFixed(2)} committed by {cost.commitment_count} {cost.commitment_count === 1 ? 'person' : 'people'}
                                </p>
                              ) : (
                                <p className="text-[#666] text-xs">No commitments yet</p>
                              )}
                            </div>
                            {cost.commitment_count > 0 && (
                              <button
                                onClick={() => toggleExpandCost(cost.id)}
                                className="flex items-center gap-1 text-[#FFD700] text-xs hover:text-white transition-colors"
                              >
                                {expandedCost === cost.id ? (
                                  <>
                                    <ChevronUp className="w-3 h-3" /> Hide Details
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-3 h-3" /> View Details
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          
                          {/* Expanded Individual Commitments */}
                          {expandedCost === cost.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              className="mt-3 space-y-2"
                            >
                              {loadingCommitments[cost.id] ? (
                                <div className="flex items-center gap-2 py-2">
                                  <RefreshCw className="w-4 h-4 text-[#FFD700] animate-spin" />
                                  <span className="text-[#666] text-xs">Loading...</span>
                                </div>
                              ) : commitments[cost.id]?.length > 0 ? (
                                commitments[cost.id].map((commitment) => (
                                  <div
                                    key={commitment.id}
                                    className={`p-2 border text-xs ${
                                      commitment.hidden 
                                        ? 'bg-[#1A1C18]/50 border-[#333] opacity-60' 
                                        : 'bg-[#1A1C18] border-[#333]'
                                    }`}
                                  >
                                    {editingIndividualCommitment === commitment.id ? (
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <User className="w-3 h-3 text-[#666]" />
                                          <input
                                            type="text"
                                            value={editIndividualData.name}
                                            onChange={(e) => setEditIndividualData(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="Name"
                                            className="flex-1 px-2 py-1 bg-[#0A0A0A] border border-[#4A5320] text-white text-xs focus:outline-none focus:border-[#FFD700]"
                                          />
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <DollarSign className="w-3 h-3 text-[#666]" />
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={editIndividualData.amount}
                                            onChange={(e) => setEditIndividualData(prev => ({ ...prev, amount: e.target.value }))}
                                            className="flex-1 px-2 py-1 bg-[#0A0A0A] border border-[#4A5320] text-white text-xs focus:outline-none focus:border-[#FFD700]"
                                          />
                                        </div>
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => saveIndividualCommitment(commitment.id, cost.id)}
                                            className="flex items-center gap-1 px-2 py-1 bg-green-700 text-white text-xs hover:bg-green-600"
                                          >
                                            <Save className="w-3 h-3" /> Save
                                          </button>
                                          <button
                                            onClick={cancelEditingIndividualCommitment}
                                            className="flex items-center gap-1 px-2 py-1 bg-[#333] text-white text-xs hover:bg-[#444]"
                                          >
                                            <X className="w-3 h-3" /> Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          {commitment.hidden && (
                                            <span className="text-[#666] text-xs px-1 py-0.5 bg-[#333] rounded">HIDDEN</span>
                                          )}
                                          <div className="flex items-center gap-1">
                                            <User className="w-3 h-3 text-[#666]" />
                                            <span className={commitment.hidden ? "text-[#666]" : "text-white"}>{commitment.name}</span>
                                          </div>
                                          <span className={commitment.hidden ? "text-[#666]" : "text-[#FFD700] font-semibold"}>
                                            ${commitment.amount.toFixed(2)}
                                          </span>
                                          <span className="text-[#666]">
                                            {new Date(commitment.created_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                        <div className="flex gap-1">
                                          <button
                                            onClick={() => startEditingIndividualCommitment(commitment)}
                                            className="p-1 text-[#FFD700] hover:text-white"
                                            title="Edit"
                                          >
                                            <Edit2 className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={() => toggleHideCommitment(commitment.id, cost.id, commitment.hidden)}
                                            className={`p-1 ${commitment.hidden ? 'text-green-400 hover:text-green-300' : 'text-[#A3A3A3] hover:text-white'}`}
                                            title={commitment.hidden ? "Unhide" : "Hide"}
                                          >
                                            {commitment.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                          </button>
                                          <button
                                            onClick={() => deleteIndividualCommitment(commitment.id, cost.id)}
                                            className="p-1 text-red-400 hover:text-red-300"
                                            title="Delete"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <p className="text-[#666] text-xs py-2">No individual commitments recorded</p>
                              )}
                            </motion.div>
                          )}
                        </div>
                        
                        <p className="text-[#666] text-xs mt-2">
                          Added: {new Date(cost.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <span className={`font-['Bebas_Neue'] text-3xl ${cost.hidden ? 'text-[#666]' : cost.is_covered ? 'text-green-400' : 'text-[#FFD700]'}`}>
                          ${cost.amount.toFixed(2)}
                        </span>
                        
                        <div className="flex gap-2 mt-3 justify-end">
                          <button
                            onClick={() => startEditingCost(cost)}
                            className="p-2 border border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700]/10 transition-colors"
                            title="Edit cost details"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleToggleHideCost(cost.id, cost.hidden)}
                            className={`p-2 border transition-colors ${
                              cost.hidden 
                                ? 'border-green-700 text-green-400 hover:bg-green-900/30' 
                                : 'border-[#666] text-[#888] hover:bg-[#333]'
                            }`}
                            title={cost.hidden ? 'Show publicly' : 'Hide from public'}
                          >
                            {cost.hidden ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => handleToggleCover(cost.id)}
                            className={`p-2 border transition-colors ${
                              cost.is_covered 
                                ? 'border-red-700 text-red-400 hover:bg-red-900/30' 
                                : 'border-green-700 text-green-400 hover:bg-green-900/30'
                            }`}
                            title={cost.is_covered ? 'Mark as not covered' : 'Mark as covered'}
                          >
                            {cost.is_covered ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => handleDelete(cost.id)}
                            className="p-2 border border-red-700 text-red-400 hover:bg-red-900/30 transition-colors"
                            title="Delete cost"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Back Link */}
        <div className="text-center mt-8 space-y-2">
          <a href="/admin/balance" className="block text-[#FFD700] font-mono text-sm uppercase tracking-widest hover:underline">
            Update Bank Balance →
          </a>
          <a href="/donate" className="block text-[#666] font-mono text-sm uppercase tracking-widest hover:text-white transition-colors">
            ← Back to Donate Page
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminCosts;
